import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAccessToken, getRefreshToken, isAccessTokenExpired, logoutAplication } from '../token/UserToken';
import { refreshTokenApi } from '../axios/api';

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  login: (accessToken: string, refreshToken: string, userId: string) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
  ensureValidToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(getAccessToken());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!getAccessToken());

  const login = (accessToken: string, refreshToken: string, userId: string) => {
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    localStorage.setItem("user-id", userId);
    setAccessToken(accessToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    logoutAplication();
    setAccessToken(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const token = getAccessToken();
    const refreshTokenValue = getRefreshToken();
    
    if (token && refreshTokenValue) {
      setAccessToken(token);
      setIsAuthenticated(true);
      
      // Verificar se o token está expirado apenas na inicialização
      if (isAccessTokenExpired()) {
        refreshTokenApi().catch(() => {
          logout();
        });
      }
    } else {
      setIsAuthenticated(false);
      setAccessToken(null);
    }
  }, []); // Remover dependência de isAuthenticated para evitar loops

  // Timer separado para verificação proativa de token
  useEffect(() => {
    // Função para verificar e renovar token proativamente
    const checkAndRefreshToken = async () => {
      const token = getAccessToken();
      const refreshTokenValue = getRefreshToken();
      
      if (token && refreshTokenValue && isAuthenticated) {
        // Verificar se o token expira nos próximos 5 minutos
        if (isAccessTokenExpired()) {
          console.log('Token expirado, renovando...');
          try {
            await refreshTokenApi();
            const newToken = getAccessToken();
            if (newToken) {
              setAccessToken(newToken);
              console.log('Token renovado com sucesso e atualizado no localStorage');
            }
          } catch (error) {
            console.error('Erro ao renovar token:', error);
            logout();
          }
        }
      }
    };

    // Configurar timer para verificação proativa do token - a cada 10 minutos
    const tokenCheckInterval = setInterval(() => {
      checkAndRefreshToken();
    }, 600000); // 10 minutos

    // Cleanup do timer
    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [isAuthenticated]);

  const refreshToken = async () => {
    try {
      await refreshTokenApi();
      const newToken = getAccessToken();
      if (newToken) {
        setAccessToken(newToken);
        setIsAuthenticated(true);
        console.log('Token manualmente renovado e localStorage atualizado');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
      throw error;
    }
  };

  // Função para garantir que sempre temos um token válido
  const ensureValidToken = async (): Promise<string | null> => {
    const currentToken = getAccessToken();
    const refreshTokenValue = getRefreshToken();
    
    if (!currentToken || !refreshTokenValue) {
      return null;
    }
    
    // Se o token está próximo do vencimento, renova
    if (isAccessTokenExpired()) {
      console.log('Token próximo do vencimento, renovando antes de usar...');
      try {
        await refreshTokenApi();
        const newToken = getAccessToken();
        if (newToken) {
          setAccessToken(newToken);
          console.log('Token renovado proativamente para uso no wrapper');
          return newToken;
        }
      } catch (error) {
        console.error('Erro ao renovar token proativamente:', error);
        logout();
        return null;
      }
    }
    
    return currentToken;
  };

  const value: AuthContextType = {
    isAuthenticated,
    accessToken,
    login,
    logout,
    refreshToken,
    ensureValidToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
